import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

const CycleCreateDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  schedules,
  handleCreatePattern,
  handleAddWeek,
  handleUpdateWeek,
  handleRemoveWeek,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("planning.cycles.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("planning.cycles.createDescription")}
          </DialogDescription>
        </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{t("planning.cycles.name")}</Label>
          <Input
            placeholder={t("planning.cycles.namePlaceholder")}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("planning.cycles.description")}</Label>
          <Textarea
            placeholder={t("planning.cycles.descriptionPlaceholder")}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("planning.cycles.duration")}</Label>
          <Input
            type="number"
            min="1"
            max="52"
            value={formData.cycle_duration_weeks}
            onChange={(e) => setFormData({ ...formData, cycle_duration_weeks: parseInt(e.target.value) || 1 })}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t("planning.cycles.weeks")}</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddWeek}
              disabled={formData.cycle_weeks.length >= formData.cycle_duration_weeks}
            >
              <Plus className="h-3 w-3 mr-1" /> {t("planning.cycles.addWeek")}
            </Button>
          </div>

          {formData.cycle_weeks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t("planning.cycles.emptyCreate")}
            </p>
          ) : (
            <div className="space-y-2">
              {formData.cycle_weeks.map((week, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Badge variant="outline" className="shrink-0">S{week.week_number}</Badge>
                  <div className="flex-1">
                    <Select
                      value={week.schedule_id?.toString()}
                      onValueChange={(value) => handleUpdateWeek(index, "schedule_id", parseInt(value))}
                    >
                      <SelectTrigger><SelectValue placeholder={t("planning.cycles.selectSchedule")} /></SelectTrigger>
                      <SelectContent>
                        {schedules.map((s) => (
                          <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveWeek(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button onClick={handleCreatePattern}>{t("planning.cycles.createAction")}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default CycleCreateDialog;
